import { useState, useEffect } from 'react';
import { Users, UserPlus, Trash2, Loader2, Key, Shield, AlertCircle, CheckCircle } from 'lucide-react';
import { usersApi } from '../../api/users';
import { useAuthStore } from '../../stores/authStore';
import type { UserListItem } from '../../types';

export function UserManagement() {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create user form
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [creating, setCreating] = useState(false);

  // Password reset modal
  const [resetPasswordUserId, setResetPasswordUserId] = useState<number | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await usersApi.getAllUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);

    try {
      await usersApi.createUser({
        username: newUsername,
        password: newPassword,
        isAdmin: newIsAdmin,
      });

      setSuccess(`User "${newUsername}" created successfully`);
      setShowCreateModal(false);
      setNewUsername('');
      setNewPassword('');
      setNewIsAdmin(false);
      await loadUsers();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId: number, username: string) => {
    if (!confirm(`Are you sure you want to delete user "${username}"? This will also delete all their votes.`)) {
      return;
    }

    setDeletingUserId(userId);
    setError(null);

    try {
      await usersApi.deleteUser(userId);
      setSuccess(`User "${username}" deleted successfully`);
      await loadUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPasswordUserId) return;

    setResettingPassword(true);
    setError(null);

    try {
      await usersApi.updatePassword(resetPasswordUserId, { password: resetPassword });
      setSuccess('Password updated successfully');
      setResetPasswordUserId(null);
      setResetPassword('');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to update password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleToggleAdmin = async (userId: number, username: string, currentIsAdmin: boolean) => {
    if (userId === currentUser?.id) {
      return; // Can't change own admin status
    }

    const action = currentIsAdmin ? 'remove admin privileges from' : 'grant admin privileges to';
    if (!confirm(`Are you sure you want to ${action} "${username}"?`)) {
      return;
    }

    setError(null);

    try {
      await usersApi.updateAdminStatus(userId, { isAdmin: !currentIsAdmin });
      setSuccess(`User "${username}" ${currentIsAdmin ? 'is no longer an admin' : 'is now an admin'}`);
      await loadUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to update admin status');
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="text-monster-blue" size={24} />
          <h2 className="text-2xl font-bold">User Management</h2>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 btn-primary text-sm"
        >
          <UserPlus size={18} />
          Create User
        </button>
      </div>

      <p className="text-sm text-gray-400 mb-4">
        Manage user accounts and permissions. Users can log in to view songs and vote on subsets.
      </p>

      {/* Success/Error Messages */}
      {error && (
        <div className="mb-4 p-3 bg-monster-crimson/10 border border-monster-crimson/30 rounded flex items-start gap-2">
          <AlertCircle className="text-monster-crimson flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-gray-300">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded flex items-start gap-2">
          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-gray-300">{success}</p>
        </div>
      )}

      {/* Users List */}
      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-monster-blue mx-auto" />
        </div>
      ) : users.length === 0 ? (
        <p className="text-center text-gray-400 py-8">No users found</p>
      ) : (
        <div className="space-y-2">
          {users.map((user) => {
            const isSelf = user.id === currentUser?.id;
            return (
              <div
                key={user.id}
                className="flex items-center gap-3 p-3 bg-monster-dark rounded hover:bg-monster-card/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{user.username}</p>
                    {isSelf && (
                      <span className="text-xs bg-monster-blue/20 text-monster-blue px-2 py-0.5 rounded">
                        You
                      </span>
                    )}
                    {user.isAdmin && (
                      <span className="text-xs bg-monster-blue px-2 py-0.5 rounded text-white">
                        Admin
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    Created {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {/* Toggle Admin */}
                  {!isSelf && (
                    <button
                      onClick={() => handleToggleAdmin(user.id, user.username, user.isAdmin)}
                      className={`w-10 h-10 flex items-center justify-center rounded ${
                        user.isAdmin
                          ? 'bg-monster-blue/20 text-monster-blue hover:bg-monster-blue/30'
                          : 'bg-monster-dark hover:bg-monster-card text-gray-400 hover:text-white'
                      } transition-all`}
                      title={user.isAdmin ? 'Remove admin privileges' : 'Grant admin privileges'}
                    >
                      <Shield size={20} />
                    </button>
                  )}

                  {/* Reset Password */}
                  <button
                    onClick={() => {
                      setResetPasswordUserId(user.id);
                      setResetPassword('');
                    }}
                    className="w-10 h-10 flex items-center justify-center rounded bg-monster-dark hover:bg-monster-card text-gray-400 hover:text-white transition-all"
                    title="Reset password"
                  >
                    <Key size={20} />
                  </button>

                  {/* Delete User */}
                  {!isSelf && (
                    <button
                      onClick={() => handleDeleteUser(user.id, user.username)}
                      disabled={deletingUserId === user.id}
                      className="w-10 h-10 flex items-center justify-center rounded bg-monster-crimson/10 hover:bg-monster-crimson text-monster-crimson hover:text-white transition-all disabled:opacity-50"
                      title="Delete user"
                    >
                      {deletingUserId === user.id ? (
                        <Loader2 size={20} className="animate-spin" />
                      ) : (
                        <Trash2 size={20} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-monster-card border border-monster-border rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Create New User</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  className="w-full px-3 py-2 bg-monster-dark border border-monster-border rounded focus:outline-none focus:border-monster-blue"
                  placeholder="Enter username"
                  pattern="[a-zA-Z0-9_\-]+"
                  title="Only letters, numbers, underscores, and hyphens allowed"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-monster-dark border border-monster-border rounded focus:outline-none focus:border-monster-blue"
                  placeholder="Enter password"
                  minLength={6}
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Minimum 6 characters</p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isAdmin"
                  checked={newIsAdmin}
                  onChange={(e) => setNewIsAdmin(e.target.checked)}
                  className="w-4 h-4 rounded bg-monster-dark border-monster-border"
                />
                <label htmlFor="isAdmin" className="text-sm font-medium text-gray-300">
                  Grant admin privileges
                </label>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewUsername('');
                    setNewPassword('');
                    setNewIsAdmin(false);
                    setError(null);
                  }}
                  disabled={creating}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="btn-primary"
                >
                  {creating ? (
                    <>
                      <Loader2 className="inline animate-spin mr-2" size={16} />
                      Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUserId !== null && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-monster-card border border-monster-border rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Reset Password</h3>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-monster-dark border border-monster-border rounded focus:outline-none focus:border-monster-blue"
                  placeholder="Enter new password"
                  minLength={6}
                  required
                  autoFocus
                />
                <p className="text-xs text-gray-400 mt-1">Minimum 6 characters</p>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setResetPasswordUserId(null);
                    setResetPassword('');
                    setError(null);
                  }}
                  disabled={resettingPassword}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resettingPassword}
                  className="btn-primary"
                >
                  {resettingPassword ? (
                    <>
                      <Loader2 className="inline animate-spin mr-2" size={16} />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
